import { useState, useEffect } from "react"
import { Card, Badge, Row, Col, Button } from "react-bootstrap"
import { CheckCircle2, Calendar, MapPin, Hash, User, Plus, Minus, UserCheck, UserX, QrCode } from "lucide-react"
import { useTranslation } from "react-i18next"
import { GuestInvitation, checkInGuests, checkOutGuests } from "@/api/scanner"

type GuestInfoProps = {
  guest: GuestInvitation
  onUpdate?: (updatedGuest: GuestInvitation) => void
  onRefetch?: () => Promise<void>
  onScanAgain?: () => void
}

const GuestInfo = ({ guest, onUpdate, onRefetch, onScanAgain }: GuestInfoProps) => {
  const { t } = useTranslation()
  
  const event = guest.event.data[0] // Get first event from array
  
  // Track checked-in guests count
  const [checkedInCount, setCheckedInCount] = useState<number>(guest.scanned_guests_number || 0)
  const [guestsToCheckIn, setGuestsToCheckIn] = useState<number>(1)
  const [isCheckingIn, setIsCheckingIn] = useState(false)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  
  // Sync local state when guest prop changes (after refetch)
  useEffect(() => {
    setCheckedInCount(guest.scanned_guests_number || 0)
  }, [guest.scanned_guests_number])
  
  // Total expected guests
  const totalExpected = guest.guests_number_max
  const remainingToCheckIn = totalExpected - checkedInCount
  
  const handleCheckIn = async () => {
    try {
      setIsCheckingIn(true)
      await checkInGuests({
        guestHash: guest.hash,
        event_id: event.event_id,
        guests_number_to_check_in: guestsToCheckIn,
      })
      console.log("✅ Checked in guests:", guestsToCheckIn)
      
      // Re-fetch fresh data from backend
      await onRefetch?.()
      
      setGuestsToCheckIn(1) // Reset to 1
    } catch (error) {
      console.error("❌ Failed to check in guests:", error)
    } finally {
      setIsCheckingIn(false)
    }
  }
  
  const handleCheckOut = async () => {
    try {
      setIsCheckingOut(true)
      await checkOutGuests({
        guestHash: guest.hash,
        event_id: event.event_id,
        guests_number_to_check_out: guestsToCheckIn,
      })
      console.log("✅ Checked out guests:", guestsToCheckIn)
      
      // Re-fetch fresh data from backend
      await onRefetch?.()
      
      setGuestsToCheckIn(1) // Reset to 1
    } catch (error) {
      console.error("❌ Failed to check out guests:", error)
    } finally {
      setIsCheckingOut(false)
    }
  }
  
  const incrementGuestsToCheckIn = () => {
    setGuestsToCheckIn((prev) => Math.min(prev + 1, remainingToCheckIn))
  }
  
  const decrementGuestsToCheckIn = () => {
    setGuestsToCheckIn((prev) => Math.max(1, prev - 1))
  }

  const isLoading = isCheckingIn || isCheckingOut

  const formatDate = (dateString: string | undefined) => {
    if (!dateString || dateString === "NULL") {
      return t("scanner.notSpecified")
    }
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return dateString
    }
  }

  const isToday = () => {
    if (!event?.start_datetime || event.start_datetime === "NULL") {
      return false
    }

    try {
      const today = new Date()
      const todayStr = today.toISOString().split("T")[0]
      const eventDate = new Date(event.start_datetime)
      const eventDateStr = eventDate.toISOString().split("T")[0]
      return todayStr === eventDateStr
    } catch {
      return false
    }
  }

  const eventIsToday = isToday()

  const getStatusBadge = () => {
    const statusMap: { [key: number]: { variant: string; text: string } } = {
      0: { variant: "secondary", text: t("scanner.status.waiting") },
      1: { variant: "success", text: t("scanner.status.confirmed") },
      2: { variant: "danger", text: t("scanner.status.declined") },
      3: { variant: "warning", text: t("scanner.status.later") },
      4: { variant: "info", text: t("scanner.status.scanned") },
      5: { variant: "primary", text: t("scanner.status.visited") },
      6: { variant: "danger", text: t("scanner.status.failed") },
    }

    const status = statusMap[guest.status] || statusMap[0]
    return <Badge bg={status.variant}>{status.text}</Badge>
  }

  return (
    <Card className="shadow-sm animate__animated animate__fadeIn">
      <Card.Body>
        <div className="pb-4 border-bottom mb-4">
          <div className="d-flex gap-3">
            {/* Checkmark - Vertically Centered */}
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
                style={{ width: "48px", height: "48px", minWidth: "48px" }}
              >
                <CheckCircle2 size={22} className="text-success" />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-grow-1 d-flex flex-column gap-1">
              {/* Status Row */}
              <div className="d-flex align-items-center justify-content-between gap-2">
                <span className="text-dark text-uppercase" style={{ fontSize: "0.9rem" }}>{t("scanner.validInvitation")}</span>
                <Badge bg={eventIsToday ? "success" : "danger"} className="px-2 py-1 fw-normal text-uppercase" style={{ fontSize: "0.9rem" }}>
                  {eventIsToday ? t("scanner.today") : t("scanner.notToday")}
                </Badge>
              </div>
              
              {/* Guest Name */}
              <h4 className="mb-0" style={{ fontWeight: 800 }}>
                {guest.first_name} {guest.last_name}
              </h4>
            </div>
          </div>
        </div>

        <Row className="text-center mb-3">
          <Col xs={6} md={3} className="mb-3 mb-md-0">
            <div className="d-flex flex-column align-items-center">
              <Calendar size={16} className="text-muted mb-1" />
              <p className="text-muted small mb-1">{t("scanner.eventName")}</p>
              <p className="fw-bold mb-0">{event.name}</p>
            </div>
          </Col>

          <Col xs={6} md={3} className="mb-3 mb-md-0">
            <div className="d-flex flex-column align-items-center">
              <MapPin size={16} className="text-muted mb-1" />
              <p className="text-muted small mb-1">{t("scanner.zone")}</p>
              <p className="fw-bold mb-0">{guest.zone || t("scanner.notSpecified")}</p>
            </div>
          </Col>

          <Col xs={6} md={3} className="mb-3 mb-md-0">
            <div className="d-flex flex-column align-items-center">
              <User size={16} className="text-muted mb-1" />
              <p className="text-muted small mb-1">{t("scanner.expectedTotal")}</p>
              <p className="fw-bold mb-0">{totalExpected}</p>
            </div>
          </Col>

          <Col xs={6} md={3}>
            <div className="d-flex flex-column align-items-center">
              <UserCheck size={16} className="text-muted mb-1" />
              <p className="text-muted small mb-1">{t("scanner.checkedIn")}</p>
              <p className="fw-bold mb-0">{checkedInCount}</p>
            </div>
          </Col>
        </Row>

        {/* Check-in/Check-out Controls */}
        <div className="border-top pt-3 mb-3">
          <div className="text-center">
            <div className="mb-3 d-flex flex-column align-items-center gap-1">
              <p className="text-muted small mb-0 fw-semibold">{t("scanner.selectGuestsNumber")}</p>
              {remainingToCheckIn > 0 && (
                <Badge bg="primary" className="fs-6 px-3 py-1">
                  {remainingToCheckIn} {t("scanner.remainingToCheckIn")}
                </Badge>
              )}
              {remainingToCheckIn === 0 && checkedInCount > 0 && (
                <Badge bg="success" className="fs-6 px-3 py-1">
                  {t("scanner.allGuestsCheckedIn")}
                </Badge>
              )}
            </div>
            
            {/* Number selector */}
            <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
              <Button
                variant="outline-secondary"
                onClick={decrementGuestsToCheckIn}
                disabled={guestsToCheckIn === 1 || isLoading}
                className="rounded-circle d-flex align-items-center justify-content-center p-0 border"
                style={{ 
                  width: "50px", 
                  height: "50px",
                  minWidth: "50px",
                  minHeight: "50px",
                  maxWidth: "50px",
                  maxHeight: "50px"
                }}
              >
                <Minus size={20} />
              </Button>
              
              <div className="text-center">
                <div
                  className="d-flex align-items-center justify-content-center fw-bold bg-primary bg-opacity-10 rounded"
                  style={{ fontSize: "2.5rem", width: "100px", height: "100px" }}
                >
                  {guestsToCheckIn}
                </div>
                <p className="text-muted small mt-2 mb-0">
                  {t("scanner.guestsToProcess")}
                </p>
              </div>
              
              <Button
                variant="outline-secondary"
                onClick={incrementGuestsToCheckIn}
                disabled={guestsToCheckIn >= remainingToCheckIn || isLoading}
                className="rounded-circle d-flex align-items-center justify-content-center p-0 border"
                style={{ 
                  width: "50px", 
                  height: "50px",
                  minWidth: "50px",
                  minHeight: "50px",
                  maxWidth: "50px",
                  maxHeight: "50px"
                }}
              >
                <Plus size={20} />
              </Button>
            </div>
            
            {/* Check-in/Check-out buttons */}
            <div className="d-flex flex-wrap gap-2 justify-content-center mb-3">
              <Button
                variant="success"
                size="lg"
                onClick={handleCheckIn}
                disabled={isLoading || remainingToCheckIn === 0}
                className="d-flex align-items-center justify-content-center flex-grow-1"
                style={{ minWidth: "140px", maxWidth: "180px" }}
              >
                <UserCheck size={18} className="me-2" />
                {isCheckingIn ? t("scanner.checkingIn") : t("scanner.checkIn")}
              </Button>
              
              <Button
                variant="danger"
                size="lg"
                onClick={handleCheckOut}
                disabled={isLoading || checkedInCount === 0}
                className="d-flex align-items-center justify-content-center flex-grow-1"
                style={{ minWidth: "140px", maxWidth: "180px" }}
              >
                <UserX size={18} className="me-2" />
                {isCheckingOut ? t("scanner.checkingOut") : t("scanner.checkOut")}
              </Button>
            </div>
            
            {/* Scan Again Button - Full Width */}
            {onScanAgain && (
              <Button 
                variant="dark" 
                size="lg"
                onClick={onScanAgain}
                className="w-100 d-flex align-items-center justify-content-center"
              >
                <QrCode size={20} className="me-2" />
                {t("scanner.scanAgain")}
              </Button>
            )}
          </div>
        </div>

        {event && (
          <div className="border-top pt-3">
            <div className="d-flex align-items-start gap-2 mb-2">
              <Hash size={16} className="text-muted mt-1 flex-shrink-0" />
              <div className="flex-grow-1">
                <p className="text-muted small mb-0">{t("scanner.eventId")}</p>
                <p className="fw-semibold mb-0">{event.event_id}</p>
              </div>
            </div>
            <div className="d-flex align-items-start gap-2 mb-2">
              <MapPin size={16} className="text-muted mt-1 flex-shrink-0" />
              <div className="flex-grow-1">
                <p className="text-muted small mb-0">{t("scanner.venue")}</p>
                <p className="mb-0">{event.address?.address || t("scanner.notSpecified")}</p>
              </div>
            </div>
            {event.start_datetime && (
              <div className="d-flex align-items-start gap-2">
                <Calendar size={16} className="text-muted mt-1 flex-shrink-0" />
                <div className="flex-grow-1">
                  <p className="text-muted small mb-0">{t("scanner.eventDate")}</p>
                  <p className="mb-0">{formatDate(event.start_datetime)}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default GuestInfo
